import React, { useCallback, useEffect, useState } from 'react';
import { getPersoneller, getIzinHafta, izinOlustur, izinAralikOlustur, izinGuncelle, izinSil, getYarimGunSecenekleri } from '../services/api';
import { haftaBaslangici, toISODate, tarihTR, gunAdi, haftalarByAy, ayYYYYMM, haftaEtiket } from '../utils/hafta';

const bosForm = { dokKod: '', izinTarihi: '', bitisTarihi: '', izinTuru: 'TAM_GUN', baslangicSaat: '', bitisSaat: '' };

// Bu türler haftalık 1,5 gün kotasının dışındadır; yeni kayıt oluştururken tek gün yerine
// bir tarih aralığı seçilip aralıktaki her gün için ayrı kayıt tek seferde oluşturulur.
const KOTA_DISI_TURLER = ['YILLIK_IZIN', 'UCRETSIZ_IZIN', 'RAPORLU_IZIN'];

const IZIN_TURU_ETIKET = {
  TAM_GUN: 'Tam Gün',
  YARIM_GUN: 'Yarım Gün',
  YILLIK_IZIN: 'Yıllık İzin',
  UCRETSIZ_IZIN: 'Ücretsiz İzin',
  RAPORLU_IZIN: 'Raporlu İzin',
};

const IZIN_TURU_BADGE = {
  TAM_GUN: 'badge-tamgun',
  YARIM_GUN: 'badge-yarimgun',
  YILLIK_IZIN: 'badge-tamgun',
  UCRETSIZ_IZIN: 'badge-tamgun',
  RAPORLU_IZIN: 'badge-tamgun',
};

export default function IzinYonetimi() {
  const [ayGirdi, setAyGirdi] = useState(ayYYYYMM(new Date()));
  const [seciliHaftaISO, setSeciliHaftaISO] = useState(toISODate(haftaBaslangici(new Date())));
  const [personelFiltre, setPersonelFiltre] = useState('');
  const [personeller, setPersoneller] = useState([]);
  const [kayitlar, setKayitlar] = useState([]);
  const [form, setForm] = useState(bosForm);
  const [editId, setEditId] = useState(null);
  const [modalAcik, setModalAcik] = useState(false);
  const [hata, setHata] = useState('');
  const [basari, setBasari] = useState('');
  const [yukleniyor, setYukleniyor] = useState(false);
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [yarimGunSecenekleri, setYarimGunSecenekleri] = useState([]);

  const haftalar = haftalarByAy(ayGirdi);
  const hafta = haftaBaslangici(seciliHaftaISO);
  const haftaBitis = new Date(hafta.getTime() + 6 * 86400000);

  const gosterilenKayitlar = personelFiltre
    ? kayitlar.filter(k => String(k.dokKod) === String(personelFiltre))
    : kayitlar;

  useEffect(() => {
    getPersoneller().then(res => setPersoneller(res.data)).catch(() => {});
  }, []);

  const listeYukle = useCallback(() => {
    setYukleniyor(true);
    getIzinHafta(seciliHaftaISO)
      .then(res => setKayitlar(res.data))
      .catch(() => setHata('İzin kayıtları yüklenemedi.'))
      .finally(() => setYukleniyor(false));
  }, [seciliHaftaISO]);

  useEffect(() => { listeYukle(); }, [listeYukle]);

  useEffect(() => {
    if (form.izinTuru !== 'YARIM_GUN' || !form.izinTarihi) { setYarimGunSecenekleri([]); return; }
    getYarimGunSecenekleri(form.izinTarihi)
      .then(res => setYarimGunSecenekleri(res.data))
      .catch(() => setYarimGunSecenekleri([]));
  }, [form.izinTuru, form.izinTarihi]);

  const modalAc = () => {
    setForm({ ...bosForm, izinTarihi: toISODate(new Date()) });
    setEditId(null);
    setHata(''); setBasari('');
    setModalAcik(true);
  };

  const modalKapat = () => {
    setModalAcik(false);
    setForm(bosForm);
    setEditId(null);
  };

  const duzenle = (kayit) => {
    setEditId(kayit.id);
    setForm({
      dokKod: kayit.dokKod,
      izinTarihi: toISODate(new Date(kayit.izinTarihi)),
      izinTuru: kayit.izinTuru,
      baslangicSaat: kayit.baslangicSaat || '',
      bitisSaat: kayit.bitisSaat || '',
    });
    setHata(''); setBasari('');
    setModalAcik(true);
  };

  const araligIzinMi = !editId && KOTA_DISI_TURLER.includes(form.izinTuru);

  const kaydet = async (e) => {
    e.preventDefault();
    setHata('');
    if (!form.dokKod) { setHata('Lütfen personel seçiniz.'); return; }
    if (!form.izinTarihi) { setHata(araligIzinMi ? 'Lütfen başlangıç tarihi seçiniz.' : 'Lütfen izin tarihi seçiniz.'); return; }

    if (araligIzinMi) {
      if (!form.bitisTarihi) { setHata('Lütfen bitiş tarihi seçiniz.'); return; }
      if (form.bitisTarihi < form.izinTarihi) { setHata('Bitiş tarihi başlangıç tarihinden önce olamaz.'); return; }

      setKaydediliyor(true);
      try {
        const res = await izinAralikOlustur({
          dokKod: parseInt(form.dokKod),
          izinTuru: form.izinTuru,
          baslangicTarihi: form.izinTarihi,
          bitisTarihi: form.bitisTarihi,
        });
        setBasari(`${res.data.olusturulanSayisi} günlük izin kaydı oluşturuldu.`);
        modalKapat();
        listeYukle();
      } catch (err) {
        setHata(err.response?.data?.hata || 'İşlem gerçekleştirilemedi.');
      } finally {
        setKaydediliyor(false);
      }
      return;
    }

    const dto = {
      dokKod: parseInt(form.dokKod),
      haftaBaslangici: seciliHaftaISO,
      izinTarihi: form.izinTarihi,
      izinTuru: form.izinTuru,
      baslangicSaat: form.izinTuru === 'YARIM_GUN' ? form.baslangicSaat : null,
      bitisSaat: form.izinTuru === 'YARIM_GUN' ? form.bitisSaat : null,
    };

    setKaydediliyor(true);
    try {
      if (editId) {
        await izinGuncelle(editId, dto);
        setBasari('İzin kaydı güncellendi.');
      } else {
        await izinOlustur(dto);
        setBasari('İzin kaydı oluşturuldu.');
      }
      modalKapat();
      listeYukle();
    } catch (err) {
      setHata(err.response?.data?.hata || 'İşlem gerçekleştirilemedi.');
    } finally {
      setKaydediliyor(false);
    }
  };

  const sil = async (id) => {
    if (!window.confirm('Bu izin kaydını silmek istediğinize emin misiniz?')) return;
    setHata(''); setBasari('');
    try {
      await izinSil(id);
      setBasari('İzin kaydı silindi.');
      listeYukle();
    } catch (err) {
      setHata(err.response?.data?.hata || 'Silme işlemi başarısız.');
    }
  };

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto' }}>
      <h2 style={{ marginBottom: 16, color: 'var(--med-dark)' }}>İzin Yönetimi</h2>

      {hata && !modalAcik && <div className="hata-kutu">{hata}</div>}
      {basari && <div className="basari-kutu">{basari}</div>}

      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 320 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>
              Ay Seç
            </label>
            <input type="month" value={ayGirdi} onChange={e => setAyGirdi(e.target.value)} style={{ width: '100%' }} />
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>
              Hafta Seç
            </label>
            <select value={seciliHaftaISO} onChange={e => setSeciliHaftaISO(e.target.value)} style={{ width: '100%' }}>
              {haftalar.map(h => {
                const hISO = toISODate(h);
                return (
                  <option key={hISO} value={hISO}>
                    {tarihTR(h)} – {tarihTR(new Date(h.getTime() + 6 * 86400000))}{haftaEtiket(hISO)}
                  </option>
                );
              })}
            </select>
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>
              Personel Seç
            </label>
            <select value={personelFiltre} onChange={e => setPersonelFiltre(e.target.value)} style={{ width: '100%' }}>
              <option value="">Tüm Personel</option>
              {personeller.map(p => <option key={p.kod} value={p.kod}>{p.ad}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ color: 'var(--med-dark)', margin: 0 }}>
          {tarihTR(hafta)} – {tarihTR(haftaBitis)} Haftasının İzin Kayıtları
        </h3>
        <button onClick={modalAc} className="btn btn-primary">+ Yeni İzin Kaydı</button>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {yukleniyor ? <div className="loading">Yükleniyor...</div> : (
          gosterilenKayitlar.length === 0 ? <div className="empty">Bu haftaya ait izin kaydı yok.</div> : (
            <table className="veri-tablosu">
              <thead>
                <tr>
                  <th>Personel</th>
                  <th>Gün</th>
                  <th>Tarih</th>
                  <th>Tür</th>
                  <th>Saat</th>
                  <th>Değer</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {gosterilenKayitlar.map(k => (
                  <tr key={k.id}>
                    <td>{k.personelAdi || k.dokKod}</td>
                    <td>{gunAdi(k.izinTarihi)}</td>
                    <td>{tarihTR(k.izinTarihi)}</td>
                    <td>
                      <span className={`badge ${IZIN_TURU_BADGE[k.izinTuru] || ''}`}>
                        {IZIN_TURU_ETIKET[k.izinTuru] || k.izinTuru}
                      </span>
                    </td>
                    <td>{k.izinTuru === 'YARIM_GUN' ? `${k.baslangicSaat}–${k.bitisSaat}` : '-'}</td>
                    <td>{k.izinDegeri}</td>
                    <td>
                      <button onClick={() => duzenle(k)} className="btn btn-secondary" style={{ marginRight: 6 }}>Değiştir</button>
                      <button onClick={() => sil(k.id)} className="btn btn-danger">Sil</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )}
      </div>

      {modalAcik && (
        <div
          onClick={modalKapat}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(13,43,94,0.35)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
          }}
        >
          <form onSubmit={kaydet} onClick={e => e.stopPropagation()} className="card" style={{ width: 380, background: '#fff' }}>
            <h3 style={{ marginTop: 0, marginBottom: 14, color: 'var(--med-dark)' }}>
              {editId ? 'İzin Kaydını Değiştir' : 'Yeni İzin Kaydı'}
            </h3>

            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Personel</label>
              <select value={form.dokKod} onChange={e => setForm({ ...form, dokKod: e.target.value })}>
                <option value="">Seçiniz</option>
                {personeller.map(p => <option key={p.kod} value={p.kod}>{p.ad}</option>)}
              </select>
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>İzin Türü</label>
              <select
                value={form.izinTuru}
                onChange={e => setForm({ ...form, izinTuru: e.target.value, bitisTarihi: '' })}
              >
                <option value="TAM_GUN">Tam Gün</option>
                <option value="YARIM_GUN">Yarım Gün</option>
                <option value="YILLIK_IZIN">Yıllık İzin (1,5 gün kotasına dahil değil)</option>
                <option value="UCRETSIZ_IZIN">Ücretsiz İzin (1,5 gün kotasına dahil değil)</option>
                <option value="RAPORLU_IZIN">Raporlu İzin (1,5 gün kotasına dahil değil)</option>
              </select>
              {editId && KOTA_DISI_TURLER.includes(form.izinTuru) && (
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>Mevcut bir kaydı değiştirirken yalnızca tek gün düzenlenebilir; aralık girişi yalnızca yeni kayıt oluştururken kullanılabilir.</div>
              )}
            </div>

            {araligIzinMi ? (
              <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Başlangıç Tarihi</label>
                  <input type="date" value={form.izinTarihi} onChange={e => setForm({ ...form, izinTarihi: e.target.value })} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Bitiş Tarihi</label>
                  <input type="date" value={form.bitisTarihi} onChange={e => setForm({ ...form, bitisTarihi: e.target.value })} />
                </div>
              </div>
            ) : (
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>İzin Tarihi</label>
                <input type="date" value={form.izinTarihi} onChange={e => setForm({ ...form, izinTarihi: e.target.value })} />
              </div>
            )}

            {form.izinTuru === 'YARIM_GUN' && (
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Yarım Gün Seçeneği</label>
                {form.izinTarihi && yarimGunSecenekleri.length === 0 ? (
                  <div style={{ color: '#A32D2D', fontSize: 12, paddingTop: 4 }}>
                    Pazar günü yarım gün izin kullanılamaz, yalnızca tam gün izin kullanılabilir.
                  </div>
                ) : (
                  <select
                    value={form.baslangicSaat && form.bitisSaat ? `${form.baslangicSaat}|${form.bitisSaat}` : ''}
                    onChange={e => {
                      const [b, s] = e.target.value.split('|');
                      setForm({ ...form, baslangicSaat: b || '', bitisSaat: s || '' });
                    }}
                  >
                    <option value="">Seçiniz</option>
                    {yarimGunSecenekleri.map(s => (
                      <option key={s.baslangicSaat} value={`${s.baslangicSaat}|${s.bitisSaat}`}>{s.etiket}</option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {hata && <div className="hata-kutu">{hata}</div>}

            <button type="submit" disabled={kaydediliyor} className="btn btn-primary" style={{ width: '100%', marginBottom: 8 }}>
              {kaydediliyor ? 'Kaydediliyor...' : (editId ? 'Güncelle' : 'Kaydet')}
            </button>
            <button type="button" onClick={modalKapat} className="btn btn-secondary" style={{ width: '100%' }}>
              Vazgeç
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
